// Counts real thinking time. Thinking is quiet, so we don't require clicks: time counts while the page is visible
// and focused, and stops after three minutes with no touch, key or pointer movement at all.

import { useEffect, useRef, useState } from "react";
import type { Family } from "../../shared/model";
import * as store from "./store";
import { useStore } from "./store";

const IDLE_MS = 3 * 60 * 1000;
const FLUSH_EVERY = 5;

/**
 * `creditPuzzle` false means the time still counts toward today's minutes but not toward the puzzle's own clock
 * (used when someone keeps exploring a puzzle they have already solved: the solve time stays as it was).
 */
export function useThinkTimer(family: Family, puzzleId: string, running: boolean, creditPuzzle = true): { seconds: number; paused: boolean } {
  const stored = useStore((d) => d.progress[puzzleId]?.seconds ?? 0);
  const pending = useRef(0);
  const last = useRef(Date.now());
  const [paused, setPaused] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!running) return;
    last.current = Date.now();
    const bump = () => {
      last.current = Date.now();
    };
    const events = ["pointerdown", "pointermove", "keydown", "touchstart", "wheel", "scroll"] as const;
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const flush = () => {
      if (pending.current > 0) {
        store.addThinking(family, creditPuzzle ? puzzleId : null, pending.current);
        pending.current = 0;
      }
    };
    const id = setInterval(() => {
      const visible = document.visibilityState === "visible";
      const active = visible && document.hasFocus() && Date.now() - last.current < IDLE_MS;
      setPaused(visible && !active);
      if (active) {
        pending.current += 1;
        setTick((t) => t + 1);
        if (pending.current >= FLUSH_EVERY) flush();
      }
    }, 1000);
    const onHide = () => document.visibilityState === "hidden" && flush();
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", flush);
    return () => {
      clearInterval(id);
      events.forEach((e) => window.removeEventListener(e, bump));
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", flush);
      flush();
      setPaused(false);
    };
  }, [family, puzzleId, running, creditPuzzle]);

  return { seconds: stored + (creditPuzzle ? pending.current : 0), paused };
}
